import { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useTheme } from 'uinix-ui';

import { TEXT_DELIMITED_MULTISELECT_FILTER_LIMIT } from '~/common/constants/max_lengths';
import { Actions } from '~/eds/src/components/Actions/Actions';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { Nullable } from '~/eds/src/types/Nullable';
import { PilotId } from '~/eds/src/types/PilotId';
import { UserAction } from '~/eds/src/types/UserAction';
import { EnumValue } from '~/evifields/src/types/Value';
import { useMaxHeightBasedOnDistance } from '~/hooks/useMaxHeightBasedOnDistance';
import { useTrackSegment } from '~/hooks/useTrackSegment ';

type Props = {
  fieldId: PilotId;
  values: EnumValue[];
  onBackClick?: () => void;
  onChange: (values: EnumValue[]) => void;
};
export const TextDelimitedEnumSetValue = ({
  fieldId,
  values: initialValues,
  onBackClick,
  onChange,
}: Props) => {
  const intl = useIntl();
  const trackSegment = useTrackSegment();
  const theme = useTheme();
  const maxHUpperLimit = theme.sizes['textarea.max-height'] as number;

  const [debouncedState, values, setValues] = useDebouncedState<
    Nullable<string>
  >(initialValues.join('\n'));
  const [ref, maxH] = useMaxHeightBasedOnDistance<HTMLDivElement>({
    currentMaxHeight: maxHUpperLimit,
  });

  const isLimitExceeded = useMemo(() => {
    if (!debouncedState) return false;
    return (
      debouncedState.split('\n').length >
      TEXT_DELIMITED_MULTISELECT_FILTER_LIMIT
    );
  }, [debouncedState]);

  const clear = () => {
    setValues('');
    onChange([]);
  };

  const onApply = () => {
    if (values) {
      const valuesArray = values
        .split('\n')
        .map((value) => value.trim())
        .filter((value) => !!value);

      onChange(valuesArray);
      trackSegment('textDelimitedFilterApplied', { fieldId });
    }
  };
  const actions: UserAction[] = [
    ...(onBackClick
      ? [
          {
            text: intl.formatMessage({
              id: 'Filters.TextDelimitedEnumSetValue_DEPRECATED.back',
              defaultMessage: 'Back',
              description: 'Button to go back to the select filter menu',
            }),
            level: 'tertiary' as const,
            onClick: onBackClick,
          },
        ]
      : []),
    {
      text: intl.formatMessage({
        id: 'Filters.TextDelimitedEnumSetValue_DEPRECATED.clear',
        defaultMessage: 'Clear',
        description: 'Button to clear filter values',
      }),
      level: 'tertiary',
      onClick: clear,
    },
    {
      text: intl.formatMessage({
        id: 'Filters.TextDelimitedEnumSetValue_DEPRECATED.apply',
        defaultMessage: 'Apply',
        description: 'Button to apply filter changes',
      }),
      level: 'tertiary',
      onClick: onApply,
      disabled: isLimitExceeded || !values?.length,
    },
  ];

  return (
    <Layout variant="menu.container" w={250} direction="column">
      <div ref={ref}>
        <TextArea
          name="filter-values"
          value={values}
          onChange={setValues}
          placeholder={intl.formatMessage({
            id: 'Filters.TextDelimitedEnumSetValue_DEPRECATED.placeholderEnterValues',
            defaultMessage: 'Enter values',
            description:
              'Placeholder text for a textarea where users can enter multiple values.',
          })}
          rows={6}
          maxH={maxH}
        />
      </div>
      {isLimitExceeded ? (
        <Text preset="error" variant="tiny-bold">
          {intl.formatMessage(
            {
              id: 'Filters.TextDelimitedEnumSetValue_DEPRECATED.maximumValuesExceeded',
              defaultMessage:
                'Maximum {valueLimit} values. Remove additional values to search',
              description:
                'Error message below a text area when the limit of entered values is exceeded.. Example: valueLimit = "100"',
            },
            { valueLimit: TEXT_DELIMITED_MULTISELECT_FILTER_LIMIT },
          )}
        </Text>
      ) : (
        <Text preset="description">
          {intl.formatMessage(
            {
              id: 'Filters.TextDelimitedEnumSetValue_DEPRECATED.enterValues',
              defaultMessage:
                'Paste up to {valueLimit} line-separated values. Press Shift + Return to add a new line.',
              description:
                'Textarea label that explains how many values can be entered, and how to add a new line. Example: valueLimit = "100"',
            },
            { valueLimit: TEXT_DELIMITED_MULTISELECT_FILTER_LIMIT },
          )}
        </Text>
      )}
      <Layout alignSelf="flex-end">
        <Actions actions={actions} />
      </Layout>
    </Layout>
  );
};
